// Phase A-6 PR 1 — Plumbing only. New types + resolveSignals adapter.
//
// This module introduces the unified scoring pipeline data model. PR 1 ships
// types and a pure adapter (ConfidenceContext + EdgeTierSignal → ResolvedSignals)
// with NO behavior change. computeFitScore + generateCardContent land in PR 2;
// the UNIFIED_SCORING flag flip lands in PR 3.
//
// Per memory: LB generators untouched, UI shape unchanged. Generators don't
// import this module; PurchaseConfidenceCard.tsx consumes ConfidenceAssessment
// unchanged.

import type {
  ConfidenceContext,
  EdgeTierSignal,
  FitTypeModifier,
} from "./guestReasoningTemplates";
import {
  STYLING_INTELLIGENCE,
  findBestStylingPattern,
  interpolateItem,
  normalizeBodyShape,
  normalizeBodySize,
  normalizeHeightBucket,
  type HeightBucket,
} from "./stylingPatterns";
import { normalizeItemName } from "./itemNameNormalizer";


// ---- TierTag enum (priority ordering) -----------------------------------

export type TierTag =
  | "T0_USER_SIZE"
  | "T1_PURCHASE_OUTCOMES"
  | "T2_GARMENT_SIZE"
  | "T2_GARMENT_FIT_COPY"
  | "T2_GARMENT_ATTRIBUTES" // reserved for A-7
  | "T3_BRAND_TENDENCY"
  | "T3_BRAND_CHART"
  | "T3_BRAND_MODIFIER"
  | "T4_USER_PROFILE"
  | "T5_CATEGORY_DEFAULT";

/** Priority weight — higher = stronger driver of headline/dominant_tier. */
export const TIER_PRIORITY: Record<TierTag, number> = {
  T1_PURCHASE_OUTCOMES: 100,
  T2_GARMENT_SIZE: 80,
  T3_BRAND_CHART: 70,
  T2_GARMENT_FIT_COPY: 60,
  T3_BRAND_TENDENCY: 55,
  T3_BRAND_MODIFIER: 50,
  T2_GARMENT_ATTRIBUTES: 40, // reserved A-7
  T4_USER_PROFILE: 20,
  T5_CATEGORY_DEFAULT: 10,
  T0_USER_SIZE: 0,
};

// ---- FitScore -----------------------------------------------------------

export interface FitScore {
  value: number; // 0-100
  tiers_fired: TierTag[]; // sorted by priority desc
  dominant_tier: TierTag;
  adjustment: "up" | "down" | "none";
  adjustment_magnitude: "slight" | "moderate" | "significant" | null;
  base_size: string;
  recommended_size: string;
  // A-7b PR 6a — reserved fields for T2_GARMENT_ATTRIBUTES tier output.
  // Populated by PR 6b once findBestStylingPattern is wired into
  // computeFitScore. Optional + nullable to keep PR 6a a pure plumbing add.
  styling_pattern_id?: string | null;
  styling_copy?: string | null;
  // A-7b PR 6c-3 — LLM fallback metadata.
  // - 'pattern' = hardcoded matrix matched
  // - 'pending' = no pattern; client should try LLM fallback (sparkle row
  //   may appear async via useStylingNoteLLM hook)
  // - undefined = styling intelligence didn't run or category unsupported
  styling_source?: "pattern" | "llm" | "pending";
  styling_pending?: boolean;
}

// ---- PQ-5c — Lens system v1 ---------------------------------------------

/**
 * Lens read value. Encodes a +/− evidence direction with magnitude.
 * - "++" strong positive, "+" positive, "neutral" inconclusive,
 *   "-" negative, "--" strong negative, null = not applicable.
 */
export type LensRead = "++" | "+" | "neutral" | "-" | "--" | null;

export interface Lens {
  read: LensRead;
  copy: string | null;
}

export interface LensSystem {
  fit: Lens | null;
  suits_you: Lens | null;
  /** PQ-5d — versatility derived from LB3 outcome. */
  pairs_with: Lens | null;
  /** PQ-5d — positive-only wardrobe-gap signal. */
  fills_gap: Lens | null;
}




// ---- Garment attributes (RESERVED FOR A-7 — plumbed only) ---------------

// A-7a PR 5c — structured garment attributes plumbed from detected_items
// (7 vision-extracted enums) + garment_data.fabric_stretch_enum. Pure
// pass-through; T2_GARMENT_ATTRIBUTES scoring tier does not consume these
// yet — PR 5c is plumbing only.
export interface GarmentAttributes {
  silhouette: string | null;
  length: string | null;
  neckline: string | null;
  sleeve_length: string | null;
  fit: string | null;
  fabric_weight: string | null;
  structure: string | null;
  fabric_stretch_enum: "none" | "light" | "moderate" | "high" | null;
}

// ---- ResolvedSignals (input to scoring) ---------------------------------

export type ResolvedCategory = "tops" | "bottoms" | "dresses";
export type ResolvedGender = "women" | "men";
export type ResolvedHeight = "petite" | "average" | "tall";
export type ResolvedBodySize = "slim" | "average" | "broad";
export type FabricStretch = "none" | "slight" | "moderate" | "significant";
export type SizeChartShape = Record<string, Record<string, unknown>>;

export interface ResolvedSignals {
  // User profile signals (T0, T4)
  user_size: string | null;
  user_height: ResolvedHeight | null;
  body_shape: string | null;
  body_size: ResolvedBodySize | null;
  gender: ResolvedGender | null;

  // Retailer routing
  retailer_canonical: string | null;
  category: ResolvedCategory | null;

  // Outcomes (T1) — sourced from edge tier signal
  perfect_outcomes_count: number;
  poor_outcomes_count: number;

  // Garment signals (T2)
  garment_fit_type: string | null;
  garment_fit_copy_text: string | null;
  garment_fabric: string | null;
  garment_fabric_stretch: FabricStretch | null;
  garment_size_chart: SizeChartShape | null;
  garment_model_reference: string | null;

  // Garment attributes — RESERVED FOR A-7 (pass-through only)
  garment_attributes: GarmentAttributes | null;

  // A-7b PR 6a — item name (for {item} interpolation in styling row copy).
  // Precedence: detected_items.name (heroItem.name) → garment_data.product_name.
  // Consumed by `normalizeItemName(item_name, category)` in PR 6b.
  item_name: string | null;

  // A-7b PR 6a — bucketed height for height-gated styling patterns
  // (T12, B5, B6, D7, D10, D11, O4, O7, J3, K3). Derived from raw
  // ctx.user_height via `normalizeHeightBucket` so the matcher takes a
  // stable enum.
  height_bucket: HeightBucket | null;

  // Brand signals (T3)
  brand_fit_tendency: "runs_small" | "true_to_size" | "runs_large" | null;
  brand_fit_profile_modifiers: FitTypeModifier[];
  brand_size_chart: SizeChartShape | null;
  brand_size_chart_convention: "numeric" | "letter" | "mixed" | null;

  // Server-emitted reasoning rows (raw, pre-dedup)
  server_warning_rows: string[];
  server_positive_rows: string[];
  server_check_rows: string[];
  server_recommended_size: string | null;
  server_confidence_level: "high" | "medium-high" | "medium" | "low" | null;
  server_has_brand_fit_signal: boolean;
}


const CONFIDENCE_DEBUG_KEY = "l2f_confidence_debug";

function shouldLogConfidenceDebug(): boolean {
  try {
    return (
      globalThis.localStorage?.getItem(CONFIDENCE_DEBUG_KEY) === "1" ||
      (typeof globalThis.location !== "undefined" &&
        new URLSearchParams(globalThis.location.search).get(CONFIDENCE_DEBUG_KEY) === "1")
    );
  } catch {
    return false;
  }
}

function confidenceDebug(label: string, payload: Record<string, unknown>): void {
  if (shouldLogConfidenceDebug()) console.log(`[confidence-debug] ${label}`, payload);
}

// ---- helpers ------------------------------------------------------------

function normaliseCategory(raw: string | null | undefined): ResolvedCategory | null {
  const v = (raw ?? "").toLowerCase().trim();
  if (!v) return null;
  if (v === "bottom" || v === "bottoms") return "bottoms";
  if (v === "dress" || v === "dresses") return "dresses";
  if (v === "top" || v === "tops") return "tops";
  return null;
}

function normaliseHeight(raw: string | null | undefined): ResolvedHeight | null {
  const v = (raw ?? "").toLowerCase().trim();
  if (v === "petite" || v === "average" || v === "tall") return v;
  return null;
}

function normaliseBodySize(raw: string | null | undefined): ResolvedBodySize | null {
  const v = (raw ?? "").toLowerCase().trim();
  if (v === "slim" || v === "average" || v === "broad") return v;
  return null;
}

function normaliseGenderForScoring(
  ctx: ConfidenceContext,
): ResolvedGender | null {
  const garment = ctx.garment_gender;
  const override = ctx.gender_override;
  const profile = ctx.user_gender;
  if (garment === "men" || garment === "women") return garment;
  if (override === "men" || override === "women") return override;
  if (profile === "men") return "men";
  if (profile === "women" || profile === "non-binary") return "women";
  return null;
}

function deriveOutcomeCounts(
  edge: EdgeTierSignal | null | undefined,
): { perfect: number; poor: number } {
  if (!edge?.data_sources?.has_outcomes) return { perfect: 0, poor: 0 };
  const total = edge.data_sources.outcome_count ?? 0;
  const level = edge.confidence_level ?? null;
  // Heuristic: high/medium-high outcome level → perfect; low → poor; medium
  // → neither (insufficient signal). PR 2 may refine if server splits these.
  if (level === "high" || level === "medium-high") {
    return { perfect: total, poor: 0 };
  }
  if (level === "low") {
    return { perfect: 0, poor: total };
  }
  return { perfect: 0, poor: 0 };
}

function asChartShape(v: unknown): SizeChartShape | null {
  if (!v || typeof v !== "object") return null;
  return v as SizeChartShape;
}

function pickFabricStretch(
  ctx: ConfidenceContext,
): FabricStretch | null {
  const fabric = ctx.fabric_composition;
  if (!fabric) return null;
  const lower = fabric.toLowerCase();
  // Heuristic: presence-of-stretch via shared helper terms. PR 2 returns
  // "moderate" by default when a stretch fibre is detected; "significant"
  // when content is explicitly high (≥10% elastane/spandex/lycra).
  const stretchTerms = ["elastane", "spandex", "lycra", "stretch"];
  const hasStretch = stretchTerms.some((t) => lower.includes(t));
  if (!hasStretch) return "none";
  const pctMatch = lower.match(/(\d+(?:\.\d+)?)\s*%\s*(?:elastane|spandex|lycra)/);
  if (pctMatch) {
    const pct = parseFloat(pctMatch[1]);
    if (pct >= 10) return "significant";
    if (pct >= 4) return "moderate";
    return "slight";
  }
  return "moderate";
}

function pickGarmentAttributes(
  ctx: ConfidenceContext,
): GarmentAttributes | null {
  // PR 5c — pull the 7 vision-extracted attrs from detected_items (carried on
  // ctx) plus the parallel fabric_stretch_enum from garment_data. Return null
  // when every field is absent so downstream scoring can fast-path skip.
  const stretchRaw = ctx.fabric_stretch_enum ?? null;
  const stretch =
    stretchRaw === "none" ||
    stretchRaw === "light" ||
    stretchRaw === "moderate" ||
    stretchRaw === "high"
      ? stretchRaw
      : null;
  const attrs: GarmentAttributes = {
    silhouette: ctx.silhouette ?? null,
    length: ctx.length ?? null,
    neckline: ctx.neckline ?? null,
    sleeve_length: ctx.sleeve_length ?? null,
    fit: ctx.fit ?? null,
    fabric_weight: ctx.fabric_weight ?? null,
    structure: ctx.structure ?? null,
    fabric_stretch_enum: stretch,
  };
  const anyPopulated = Object.values(attrs).some((v) => v != null);
  return anyPopulated ? attrs : null;
}


function pickModelReference(ctx: ConfidenceContext): string | null {
  const ref = ctx.model_reference;
  if (!ref) return null;
  const size = ref.size_worn ?? null;
  const height = ref.model_height ?? null;
  if (!size && !height) return null;
  if (size && height) return `Model ${height} wearing ${size}`;
  if (size) return `Model wearing ${size}`;
  return `Model ${height}`;
}

function pickConvention(
  raw: unknown,
): "numeric" | "letter" | "mixed" | null {
  if (raw === "numeric" || raw === "letter" || raw === "mixed") return raw;
  return null;
}

// ---- resolveSignals (main adapter) --------------------------------------

export interface ResolveSignalsArgs {
  ctx: ConfidenceContext;
  retailer_canonical?: string | null;
  /**
   * Optional pre-resolved brand chart convention. Caller (PR 2 scoring) will
   * supply this from the same row that populates `ctx.brand_size_chart`.
   */
  brand_size_chart_convention?: "numeric" | "letter" | "mixed" | null;
}

/**
 * Pure adapter: ConfidenceContext + EdgeTierSignal → ResolvedSignals.
 * No I/O, no side effects. PR 1 ships this with full unit-test coverage so
 * PR 2 scoring can consume a stable shape.
 */
export function resolveSignals(args: ResolveSignalsArgs): ResolvedSignals {
  const { ctx, retailer_canonical = null, brand_size_chart_convention = null } =
    args;
  const edge = ctx.edge_tier_signal ?? null;
  const { perfect, poor } = deriveOutcomeCounts(edge);

  const resolved: ResolvedSignals = {
    user_size: ctx.user_size ?? null,
    user_height: normaliseHeight(ctx.user_height),
    body_shape: ctx.user_body_shape ?? null,
    body_size: normaliseBodySize(ctx.user_body_size),
    gender: normaliseGenderForScoring(ctx),

    retailer_canonical: retailer_canonical ?? ctx.retailer ?? null,
    category: normaliseCategory(ctx.category),

    perfect_outcomes_count: perfect,
    poor_outcomes_count: poor,

    garment_fit_type: ctx.fit_type ?? null,
    garment_fit_copy_text: ctx.fit_copy ?? null,
    garment_fabric: ctx.fabric_composition ?? null,
    garment_fabric_stretch: pickFabricStretch(ctx),
    garment_size_chart: asChartShape(ctx.size_chart),
    garment_model_reference: pickModelReference(ctx),

    garment_attributes: pickGarmentAttributes(ctx),

    // A-7b PR 6a — primary: detected_items.name via ctx.item_name; secondary
    // fallback to garment_data.product_name not yet wired into ctx (RPC
    // expansion deferred). Card layer populates ctx.item_name from heroItem.
    item_name: ctx.item_name ?? null,
    height_bucket: normalizeHeightBucket(ctx.user_height ?? null),


    brand_fit_tendency: ctx.brand_fit_profile?.baseline_fit_tendency ?? null,
    brand_fit_profile_modifiers: ctx.brand_fit_profile?.fit_type_modifiers ?? [],
    brand_size_chart: asChartShape(ctx.brand_size_chart),
    brand_size_chart_convention: pickConvention(brand_size_chart_convention),

    server_warning_rows: edge?.warning_rows ?? [],
    server_positive_rows: edge?.positive_rows ?? [],
    server_check_rows: edge?.based_on ?? [],
    server_recommended_size: edge?.recommended_size ?? null,
    server_confidence_level: edge?.confidence_level ?? null,
    // PR 4 — fall back to ctx.brand_fit_profile presence for guest path,
    // where edge_tier_signal is null but the in-fn brand_fit_profiles lookup
    // still populates ctx.brand_fit_profile.
    server_has_brand_fit_signal:
      edge?.data_sources?.has_brand_fit_signal === true ||
      !!ctx.brand_fit_profile,
  };
  confidenceDebug("resolveSignals.brand_signal", {
    inputBrandFitProfile: ctx.brand_fit_profile ?? null,
    inputBrandSizeChart: ctx.brand_size_chart ?? null,
    outputBrandFitTendency: resolved.brand_fit_tendency ?? null,
    retailerCanonical: resolved.retailer_canonical,
    gender: resolved.gender,
    category: resolved.category,
  });
  return resolved;
}

// =========================================================================
// PR 4 — Retailer display-name map
// =========================================================================

const RETAILER_DISPLAY_EXCEPTIONS: Record<string, string> = {
  hm: "H&M",
  ms: "M&S",
  cos: "COS",
  asos: "ASOS",
  plt: "PrettyLittleThing",
  prettylittlething: "PrettyLittleThing",
  and_other_stories: "& Other Stories",
  me_em: "ME+EM",
  john_lewis: "John Lewis",
  river_island: "River Island",
  new_look: "New Look",
  house_of_fraser: "House of Fraser",
  harvey_nichols: "Harvey Nichols",
  holland_cooper: "Holland Cooper",
  michael_kors: "Michael Kors",
  pull_and_bear: "Pull&Bear",
  lands_end: "Lands' End",
  maniere_de_voir: "Manière De Voir",
};

export function retailerDisplayName(
  canonical: string | null | undefined,
): string {
  if (!canonical) return "the retailer";
  const key = canonical.toLowerCase().trim();
  if (RETAILER_DISPLAY_EXCEPTIONS[key]) return RETAILER_DISPLAY_EXCEPTIONS[key];
  return key
    .split("_")
    .map((w) => (w.length === 0 ? w : w[0].toUpperCase() + w.slice(1)))
    .join(" ");
}

// =========================================================================
// PR 2 — Numeric ↔ letter size conversion (UK)
// =========================================================================

const UK_WOMEN_LETTER_TO_NUMBER: Record<string, string> = {
  XS: "6",
  S: "8",
  M: "10",
  L: "14",
  XL: "16",
  "2XL": "18",
  "3XL": "20",
};
const UK_WOMEN_NUMBER_TO_LETTER: Record<string, string> = {
  "4": "XS",
  "6": "XS",
  "8": "S",
  "10": "M",
  "12": "M",
  "14": "L",
  "16": "XL",
  "18": "2XL",
  "20": "3XL",
};
const UK_MEN_LETTER_TO_NUMBER: Record<string, string> = {
  XS: "34",
  S: "36",
  M: "38",
  L: "40",
  XL: "42",
  "2XL": "44",
  "3XL": "46",
};
const UK_MEN_NUMBER_TO_LETTER: Record<string, string> = {
  "34": "XS",
  "36": "S",
  "38": "M",
  "40": "L",
  "42": "XL",
  "44": "2XL",
  "46": "3XL",
};

export function letterToNumber(
  letter: string,
  gender: ResolvedGender,
): string | null {
  const key = letter.toUpperCase().trim();
  const map = gender === "men" ? UK_MEN_LETTER_TO_NUMBER : UK_WOMEN_LETTER_TO_NUMBER;
  return map[key] ?? null;
}

export function numberToLetter(
  num: string,
  gender: ResolvedGender,
): string | null {
  const key = String(num).trim();
  const map = gender === "men" ? UK_MEN_NUMBER_TO_LETTER : UK_WOMEN_NUMBER_TO_LETTER;
  return map[key] ?? null;
}

// =========================================================================
// PR 2 — computeFitScore
// =========================================================================

const SIZE_ORDER_LETTER = ["XS", "S", "M", "L", "XL", "2XL", "3XL"] as const;

function stepLetter(size: string, dir: "up" | "down"): string {
  const idx = SIZE_ORDER_LETTER.indexOf(size as (typeof SIZE_ORDER_LETTER)[number]);
  if (idx === -1) return size;
  if (dir === "up") return SIZE_ORDER_LETTER[Math.min(idx + 1, SIZE_ORDER_LETTER.length - 1)];
  return SIZE_ORDER_LETTER[Math.max(idx - 1, 0)];
}

function sortTiers(tiers: TierTag[]): TierTag[] {
  return [...tiers].sort((a, b) => TIER_PRIORITY[b] - TIER_PRIORITY[a]);
}

// A-7b PR 6c-3 — Gate for the LLM styling-note fallback. Only triggers when:
//  - garment_attributes has ≥2 non-null fields (enough for the model to work with)
//  - body_shape canonicalised
//  - category in supported set
// Conservative: better to skip than fire a vague LLM call.
const LLM_FALLBACK_CATEGORIES = new Set([
  "top", "bottom", "dress", "outerwear", "jumpsuit", "playsuit",
]);

export function shouldTryLlmFallback(
  signals: ResolvedSignals,
  category: string | null,
  normalized_shape: string | null,
): boolean {
  if (!normalized_shape) return false;
  if (!category || !LLM_FALLBACK_CATEGORIES.has(category)) return false;
  const attrs = signals.garment_attributes;
  if (!attrs) return false;
  let nonNull = 0;
  for (const v of Object.values(attrs)) {
    if (v != null && v !== "") nonNull += 1;
    if (nonNull >= 2) return true;
  }
  return false;
}

/**
 * Pure scoring function — derives FitScore from ResolvedSignals.
 * Higher value = higher confidence.
 */
export function computeFitScore(signals: ResolvedSignals): FitScore {
  const tiers: TierTag[] = [];
  let value = 0;
  let adjustment: "up" | "down" | "none" = "none";
  let adjustment_magnitude: FitScore["adjustment_magnitude"] = null;

  // T0 — user has a size at all
  if (signals.user_size) tiers.push("T0_USER_SIZE");

  // T1 — purchase outcomes
  if (signals.perfect_outcomes_count >= 3) {
    tiers.push("T1_PURCHASE_OUTCOMES");
    value = Math.max(value, 80);
  } else if (signals.poor_outcomes_count >= 2) {
    tiers.push("T1_PURCHASE_OUTCOMES");
    value = Math.max(value, 35);
  }

  // T2 — garment size chart resolves user size
  if (signals.garment_size_chart && signals.user_size) {
    tiers.push("T2_GARMENT_SIZE");
    value = Math.max(value, 60);
  }

  // T2 — garment fit copy
  if (signals.garment_fit_copy_text && signals.garment_fit_copy_text.trim().length > 5) {
    tiers.push("T2_GARMENT_FIT_COPY");
    value = Math.max(value, 50);
  }

  // T3 — brand size chart available
  if (signals.brand_size_chart && signals.user_size) {
    tiers.push("T3_BRAND_CHART");
    value = Math.max(value, 65);
  }

  // T3 — brand fit tendency drives adjustment
  if (signals.brand_fit_tendency && signals.brand_fit_tendency !== "true_to_size") {
    tiers.push("T3_BRAND_TENDENCY");
    adjustment = signals.brand_fit_tendency === "runs_small" ? "up" : "down";
    adjustment_magnitude = "slight";
    value = Math.max(value, 55);
  }

  // T3 — brand fit modifier (item-specific) — strongest brand signal
  if (signals.brand_fit_profile_modifiers.length > 0 && signals.garment_fit_type) {
    const match = signals.brand_fit_profile_modifiers.find(
      (m) => m.fit_type.toLowerCase() === signals.garment_fit_type!.toLowerCase(),
    );
    if (match) {
      tiers.push("T3_BRAND_MODIFIER");
      const mag = match.magnitude;
      if (mag === "moderately" || mag === "significantly" || mag === "dramatically") {
        adjustment_magnitude = mag === "moderately" ? "moderate" : "significant";
      }
      value = Math.max(value, 60);
    }
  }

  // T4 — user profile fields
  if (signals.user_height || signals.body_shape || signals.body_size) {
    tiers.push("T4_USER_PROFILE");
    value = Math.max(value, 30);
  }

  // T5 — category default fallback
  if (signals.category) {
    tiers.push("T5_CATEGORY_DEFAULT");
    value = Math.max(value, 15);
  }

  // Stretch fabric softens any size warning slightly
  if (signals.garment_fabric_stretch === "moderate" || signals.garment_fabric_stretch === "significant") {
    value = Math.min(100, value + 5);
  }

  // Confidence floor when no user size at all
  if (!signals.user_size) value = Math.min(value, 25);

  const tiers_fired = sortTiers(tiers);
  const dominant_tier = tiers_fired[0] ?? "T5_CATEGORY_DEFAULT";

  const base_size = signals.user_size ?? "";
  let recommended_size = base_size;
  if (base_size && adjustment !== "none") {
    recommended_size = stepLetter(base_size, adjustment);
    if (recommended_size === base_size) adjustment = "none";
  }

  const score: FitScore = {
    value,
    tiers_fired,
    dominant_tier,
    adjustment,
    adjustment_magnitude: adjustment === "none" ? null : adjustment_magnitude ?? "slight",
    base_size,
    recommended_size,
  };

  // A-7b PR 6b — T2_GARMENT_ATTRIBUTES wiring. Pure additive: pushes a tier
  // tag + styling copy, never mutates existing scoring decisions. Map
  // ResolvedCategory plural ("tops"/"bottoms"/"dresses") to pattern singular.
  if (
    STYLING_INTELLIGENCE &&
    signals.garment_attributes &&
    signals.body_shape &&
    signals.category
  ) {
    const normalized_shape = normalizeBodyShape(signals.body_shape);
    const normalized_size = normalizeBodySize(signals.body_size);
    const catSingular =
      signals.category === "tops"
        ? "top"
        : signals.category === "bottoms"
          ? "bottom"
          : signals.category === "dresses"
            ? "dress"
            : signals.category;

    if (normalized_shape) {
      const matched = findBestStylingPattern(
        signals.garment_attributes,
        normalized_shape,
        normalized_size,
        signals.height_bucket,
        catSingular,
      );
      if (matched) {
        if (!score.tiers_fired.includes("T2_GARMENT_ATTRIBUTES")) {
          score.tiers_fired = sortTiers([...score.tiers_fired, "T2_GARMENT_ATTRIBUTES"]);
          score.dominant_tier = score.tiers_fired[0] ?? "T5_CATEGORY_DEFAULT";
        }
        score.value = Math.min(100, score.value + 10);
        score.styling_pattern_id = matched.id;
        score.styling_copy = interpolateItem(
          matched.copy_template,
          normalizeItemName(signals.item_name, catSingular),
          catSingular,
        );
        score.styling_source = "pattern";
      } else if (shouldTryLlmFallback(signals, catSingular, normalized_shape)) {
        // No hardcoded pattern matched but inputs look rich enough for an LLM
        // attempt. Client hook (useStylingNoteLLM) will resolve this to a
        // sparkle row asynchronously, or drop it on validation/timeout.
        score.styling_source = "pending";
        score.styling_pending = true;
      }
    }
  }

  confidenceDebug("computeFitScore.result", {
    tiers_fired: score.tiers_fired,
    dominant_tier: score.dominant_tier,
    adjustment: score.adjustment,
    value: score.value,
    base_size: score.base_size,
    recommended_size: score.recommended_size,
    styling_pattern_id: score.styling_pattern_id ?? null,
  });
  return score;
}

// =========================================================================
// PR 2 — generateCardContent
// =========================================================================

import type {
  ConfidenceAssessment,
  ReasoningRow,
  MissingField,
  PrimaryGap,
} from "./guestReasoningTemplates";

function levelFromValue(
  v: number,
): "low" | "medium" | "medium-high" | "high" {
  if (v >= 76) return "high";
  if (v >= 51) return "medium-high";
  if (v >= 26) return "medium";
  return "low";
}

// PQ-5a — verdict mapping. Pure function that maps the existing 0-100
// confidence_score to the locked 3-state verdict (Buy / Your call / Skip).
// Thresholds locked per PQ-5a brief: ≥70 buy, 40-69 your_call, <40 skip.
// Defensive against out-of-range inputs (negative or >100).
export type Verdict = "buy" | "your_call" | "skip";

export function verdictFromScore(value: number): Verdict {
  if (value >= 70) return "buy";
  if (value >= 40) return "your_call";
  return "skip";
}

// SKIP-FLOOR-1 — Fit-predictive tier tags. If any of these fired, the
// engine has real evidence about how this item fits this user.
// T0_USER_SIZE: presence only. T4_USER_PROFILE: body-shape signal, not
// item-specific. T5_CATEGORY_DEFAULT: generic fallback.
// T2_GARMENT_ATTRIBUTES: A-7b styling pattern, not a fit signal.
const FIT_SIGNAL_TIERS: ReadonlyArray<TierTag> = [
  "T1_PURCHASE_OUTCOMES",
  "T2_GARMENT_SIZE",
  "T2_GARMENT_FIT_COPY",
  "T3_BRAND_CHART",
  "T3_BRAND_TENDENCY",
  "T3_BRAND_MODIFIER",
];

/** True if at least one fit-predictive tier fired. */
export function hasFitSignal(tiersFired: TierTag[] | null | undefined): boolean {
  if (!Array.isArray(tiersFired) || tiersFired.length === 0) return false;
  return tiersFired.some((t) => FIT_SIGNAL_TIERS.includes(t));
}

/**
 * SKIP-FLOOR-1 — Wraps verdictFromScore with a floor: if no fit-predictive
 * tier fired, the verdict cannot be "skip" — it floors at "your_call".
 *
 * Rationale: the engine composes score as max(tiers). Missing tiers
 * contribute 0, mathematically indistinguishable from a tier that fired
 * with a bad outcome. Skip is reserved for outfits where a real
 * fit-predictive tier (e.g. T1_PURCHASE_OUTCOMES with ≥2 poor outcomes)
 * actually drove the score down.
 */
export function verdictFromAssessment(
  score: number,
  tiersFired: TierTag[] | null | undefined,
): Verdict {
  const base = verdictFromScore(score);
  if (base === "skip" && !hasFitSignal(tiersFired)) return "your_call";
  return base;
}

function buildMissingFields(s: ResolvedSignals): MissingField[] {
  const m: MissingField[] = [];
  if (!s.user_size)
    m.push({ field: "size", display_label: "your usual size", priority: "high" });
  if (!s.user_height)
    m.push({ field: "height", display_label: "your height", priority: "medium" });
  if (!s.body_shape)
    m.push({
      field: s.gender === "men" ? "body_build" : "body_shape",
      display_label: s.gender === "men" ? "your build" : "your body shape",
      priority: "medium",
    });
  if (s.gender !== "men" && !s.body_size)
    m.push({ field: "body_size", display_label: "your body size", priority: "low" });
  return m;
}

// ---- PQ-5c — Lens computation -------------------------------------------

/**
 * Compute the Fit lens read + copy from tier signals.
 * - T1_PURCHASE_OUTCOMES → "--" (strong negative)
 * - T3_BRAND_CHART → "++" (strong positive)
 * - Other fit-predictive tiers → "+"
 * - Only T0/T4/T5 fired or empty → "neutral" with data-sparsity copy
 *
 * `recommendationText` is reused as the lens copy on +/++/-/--. Neutral
 * synthesizes its own honest "no sizing data" copy.
 */
export function computeFitLens(
  tiersFired: TierTag[] | string[] | null | undefined,
  recommendationText: string | null,
  userSize: string | null,
  brand: string | null = null,
  verdict: Verdict | null = null,
): Lens | null {
  if (!Array.isArray(tiersFired) || tiersFired.length === 0) return null;

  const tiers = tiersFired as string[];
  let read: LensRead;

  if (tiers.includes("T1_PURCHASE_OUTCOMES")) {
    read = "--";
  } else if (tiers.includes("T3_BRAND_CHART")) {
    read = "++";
  } else if (
    tiers.includes("T3_BRAND_TENDENCY") ||
    tiers.includes("T2_GARMENT_SIZE") ||
    tiers.includes("T3_BRAND_MODIFIER") ||
    tiers.includes("T2_GARMENT_FIT_COPY")
  ) {
    read = "+";
  } else {
    read = "neutral";
  }

  let copy: string | null;
  if (read === "neutral") {
    copy = userSize
      ? `Your size ${userSize} is a safe starting point. Check the size guide if exact fit matters.`
      : "Check the size guide for an accurate fit.";
  } else if (
    read === "--" &&
    verdict === "skip" &&
    tiers.includes("T1_PURCHASE_OUTCOMES")
  ) {
    // PQ-5f — dignity fix. Under Skip, never repeat the sizing
    // recommendation_text (e.g. "Buy size L") next to a ✗✗ glyph.
    // Describe the failure instead.
    if (userSize && brand) {
      copy = `Past purchases in size ${userSize} didn't fit at ${brand}.`;
    } else if (userSize) {
      copy = `Past purchases in size ${userSize} didn't fit recently.`;
    } else if (brand) {
      copy = `Past purchases didn't fit recently at ${brand}.`;
    } else {
      copy = "Past purchases didn't fit recently at this retailer.";
    }
  } else {
    copy = recommendationText ?? null;
  }

  return { read, copy };
}

/**
 * Compute the Suits-you lens from the styling note.
 * - Null when styling_copy is missing/empty.
 * - Suppressed on Skip verdict (consistent with PQ-5b verdict-block behavior).
 */
export function computeSuitsYouLens(
  stylingCopy: string | null | undefined,
  verdict: Verdict,
): Lens | null {
  if (!stylingCopy || !stylingCopy.trim()) return null;
  if (verdict === "skip") return null;
  return { read: "+", copy: stylingCopy.trim() };
}

/**
 * Build the full lens system from raw inputs. Returns null when no lens
 * has anything to say (so the renderer can skip the section entirely).
 */
export function buildLensSystem(args: {
  tiersFired: TierTag[] | string[] | null | undefined;
  recommendationText: string | null;
  stylingCopy: string | null | undefined;
  userSize: string | null;
  confidenceScore: number;
  brand?: string | null;
}): LensSystem | null {
  const verdict = verdictFromAssessment(args.confidenceScore, args.tiersFired as TierTag[]);
  const fit = computeFitLens(
    args.tiersFired,
    args.recommendationText,
    args.userSize,
    args.brand ?? null,
    verdict,
  );
  const suits_you = computeSuitsYouLens(args.stylingCopy, verdict);
  if (!fit && !suits_you) return null;
  return { fit, suits_you, pairs_with: null, fills_gap: null };
}

// ---- PQ-5d — Pairs with + Fills a gap lens computation -----------------

import {
  getSlotsForGender,
  resolveSlotForItem,
  userOwnsSlot,
  type Gender as TaxonomyGender,
  type TaxonomyItem,
} from "./wardrobeTaxonomy";

/** Subset of an LB3 owned item used for copy interpolation. */
export interface PairsWithOwnedItem {
  name?: string | null;
  category?: string | null;
}

/**
 * Pairs with lens — versatility signal derived from LB3 match outcome.
 *
 * - `empty_wardrobe` / null inputs → null (guest or no data; caller suppresses)
 * - `outcome_3_no_match` with wardrobe < SPARSITY_FLOOR → null (don't apologise)
 * - `outcome_3_no_match` with wardrobe ≥ floor → "−" (you have stuff, this
 *   doesn't slot in)
 * - `outcome_2_partial` → "+" with copy naming the first owned item
 * - `outcome_1_full` → "++" with optional outfit count
 *
 * Copy never exposes infrastructure ("we couldn't find...") — frames as
 * actionable evidence about the user's wardrobe.
 */
export const PAIRS_WITH_SPARSITY_FLOOR = 5;

export function computePairsWithLens(
  lb3Outcome: string | null | undefined,
  lb3OwnedItems: PairsWithOwnedItem[] | null | undefined,
  wardrobeTotal: number | null | undefined,
): Lens | null {
  if (!lb3Outcome || lb3Outcome === "empty_wardrobe") return null;
  const owned = Array.isArray(lb3OwnedItems) ? lb3OwnedItems : [];
  const total = wardrobeTotal ?? 0;

  switch (lb3Outcome) {
    case "outcome_1_full": {
      const count = owned.length;
      return {
        read: "++",
        copy:
          count >= 3
            ? `Builds ${count} complete outfits with what you own.`
            : "Builds complete outfits with what you own.",
      };
    }
    case "outcome_2_partial": {
      const first = owned[0];
      const label = (first?.name ?? first?.category ?? "").trim();
      return {
        read: "+",
        copy: label
          ? `Pairs with your ${label}.`
          : "Pairs with what you own.",
      };
    }
    case "outcome_3_no_match": {
      if (total < PAIRS_WITH_SPARSITY_FLOOR) return null;
      return {
        read: "-",
        copy: "Nothing in your wardrobe pairs cleanly yet.",
      };
    }
    default:
      return null;
  }
}

/**
 * Fills a gap lens — positive-only signal.
 *
 * Fires "+" when the hero item resolves to a wardrobe slot and the user
 * owns zero items in that slot. Returns null in every other case (empty
 * wardrobe, unknown gender, unresolved slot, slot already owned).
 *
 * Owning multiples of a slot is treated as style consolidation, never
 * surfaced as a negative.
 */
export function computeFillsGapLens(
  heroItem: TaxonomyItem | null | undefined,
  wardrobeItems: TaxonomyItem[] | null | undefined,
  gender: TaxonomyGender | null | undefined,
): Lens | null {
  if (!heroItem || !gender) return null;
  if (!Array.isArray(wardrobeItems) || wardrobeItems.length === 0) return null;
  const slot = resolveSlotForItem(heroItem, gender);
  if (!slot) return null;
  if (userOwnsSlot(slot, wardrobeItems)) return null;
  return {
    read: "+",
    copy: `You don't own a ${slot.label} yet.`,
  };
}

// Re-export taxonomy helpers for callers that want to count slot coverage.
export { getSlotsForGender, resolveSlotForItem, userOwnsSlot };
export type { TaxonomyGender, TaxonomyItem };



function buildPrimaryGap(s: ResolvedSignals, score: FitScore): PrimaryGap {
  if (!s.user_size) return "user_data";
  const hasGarment =
    !!s.garment_size_chart || !!s.garment_fit_copy_text || !!s.garment_fit_type;
  if (!hasGarment && score.value < 40) return "garment_data";
  if (!s.body_shape && !s.user_height) return "user_data";
  return "none";
}

/**
 * Convert FitScore + ResolvedSignals → ConfidenceAssessment shape. Output
 * matches legacy `generateConfidenceAssessment` shape so PurchaseConfidenceCard
 * renders identically.
 */
export function generateCardContent(
  score: FitScore,
  signals: ResolvedSignals,
): ConfidenceAssessment {
  const confidence_level = levelFromValue(score.value);
  const rows: ReasoningRow[] = [...signals.server_warning_rows.map((t) => ({
    icon: "warning" as const,
    text: t,
  })), ...signals.server_positive_rows.map((t) => ({
    icon: "check" as const,
    text: t,
  }))];

  // A-7b PR 6b — styling row appended after server warning/positive rows.
  // Existing shape-default / brand / fit rows pushed by callers downstream
  // (or via server signals) continue to appear AFTER this row.
  if (score.styling_copy) {
    rows.push({
      icon: "sparkle",
      type: "styling",
      text: score.styling_copy,
    });
  }

  // Headline
  let recommendation_type: ConfidenceAssessment["recommendation_type"];
  let recommendation_text: string;
  let reasoning: string;

  const brand = retailerDisplayName(signals.retailer_canonical);

  if (!signals.user_size) {
    recommendation_type = "prompt_user";
    recommendation_text = "Tell us your size to get a recommendation";
    reasoning = "We need your usual size to recommend a fit";
  } else if (score.adjustment !== "none" && score.recommended_size !== score.base_size) {
    recommendation_type = "adjustment";
    const dirWord = score.adjustment === "up" ? "up" : "down";
    const runsWord = score.adjustment === "up" ? "runs small" : "runs large";
    // PR 4b — subtitle driven by adjustment + brand_fit_tendency, NOT by which
    // T3 tier won dominance. When the brand also has a size chart
    // (T3_BRAND_CHART wins on priority), still use "runs small/large" wording
    // in the subtitle. Chart data belongs in reasoning_rows, not the subtitle.
    if (score.dominant_tier === "T3_BRAND_MODIFIER") {
      recommendation_text = `Consider sizing ${dirWord} to ${score.recommended_size} — ${brand}'s fit for this style`;
      reasoning = `${brand}'s fit for this style suggests sizing ${dirWord} from your usual ${score.base_size}.`;
    } else if (
      signals.brand_fit_tendency === "runs_small" ||
      signals.brand_fit_tendency === "runs_large"
    ) {
      recommendation_text = `Consider sizing ${dirWord} to ${score.recommended_size} — ${brand} typically ${runsWord}`;
      reasoning =
        score.adjustment === "up"
          ? `${brand} runs small — sizing up gives a safer fit with your usual ${score.base_size}.`
          : `${brand} runs large — sizing down keeps a cleaner fit on your usual ${score.base_size}.`;
    } else {
      recommendation_text = `Consider sizing ${dirWord} to ${score.recommended_size} from your usual ${score.base_size}`;
      reasoning = `Fit signals suggest sizing ${dirWord} from your usual ${score.base_size}.`;
    }
  } else if (score.dominant_tier === "T1_PURCHASE_OUTCOMES" && signals.perfect_outcomes_count >= 3) {
    recommendation_type = "absolute";
    recommendation_text = `Buy size ${signals.user_size} with confidence`;
    reasoning = `Fits you well — ${signals.perfect_outcomes_count} previous purchases at ${brand}.`;
  } else if (score.dominant_tier === "T3_BRAND_CHART") {
    recommendation_type = "absolute";
    recommendation_text = `Likely size ${signals.user_size} — ${brand}'s chart matches your measurements`;
    reasoning = `Based on ${brand}'s size chart for your usual ${signals.user_size}.`;
  } else if (score.dominant_tier === "T2_GARMENT_SIZE") {
    recommendation_type = "absolute";
    recommendation_text = `Buy size ${signals.user_size}`;
    reasoning = "Your usual size matches the size chart.";
  } else if (
    signals.brand_fit_tendency === "true_to_size" &&
    signals.retailer_canonical
  ) {
    recommendation_type = "hedged";
    recommendation_text = `Likely size ${signals.user_size} — your usual fit at ${brand}`;
    reasoning = `${brand} typically runs true to size.`;
  } else {
    recommendation_type = "hedged";
    recommendation_text = `Likely size ${signals.user_size} — your usual fit`;
    reasoning = `Your size ${signals.user_size} is a safe starting point. Check the size guide if exact fit matters.`;
  }

  const missing_fields = buildMissingFields(signals);
  const primary_gap = buildPrimaryGap(signals, score);

  const garment_score =
    (signals.garment_size_chart ? 20 : 0) +
    (signals.garment_fit_copy_text ? 15 : 0) +
    (signals.garment_fit_type ? 10 : 0) +
    (signals.garment_model_reference ? 5 : 0);
  const user_score =
    (signals.user_size ? 20 : 0) +
    (signals.user_height ? 10 : 0) +
    (signals.body_shape ? 10 : 0) +
    (signals.body_size ? 10 : 0);
  const brand_fit_score = signals.server_has_brand_fit_signal ? 30 : 0;

  // PQ-5c — derive lens system from the scored shape. ADD-only: existing
  // fields above are preserved verbatim; lenses sit alongside them.
  const lenses = buildLensSystem({
    tiersFired: score.tiers_fired,
    recommendationText: recommendation_text,
    stylingCopy: score.styling_copy ?? null,
    userSize: signals.user_size,
    confidenceScore: score.value,
    brand: signals.retailer_canonical ? brand : null,
  });

  return {
    recommended_size: signals.user_size ? score.recommended_size : null,
    recommendation_type,
    recommendation_text,
    reasoning,
    confidence_level,
    confidence_score: Math.round(score.value),
    garment_score,
    user_score,
    brand_fit_score,
    primary_gap,
    missing_fields,
    diagnostic_message: "",
    reasoning_rows: rows,
    brand_fit_signal: null,
    // SKIP-FLOOR-1 — surface tiers_fired so PCC can floor verdict.
    tiers_fired: score.tiers_fired,
    // PQ-5c — lens system (Fit + Suits you populated; Wardrobe + You'll wear inert).
    lenses,
    // A-7b PR 6c-3 — surface LLM fallback metadata to the card.
    styling_pending: score.styling_pending === true,
    styling_llm_payload:
      score.styling_pending === true && signals.garment_attributes
        ? {
            garment_attributes: signals.garment_attributes as unknown as Record<string, string | null>,
            item_name: signals.item_name ?? null,
            category:
              signals.category === "tops"
                ? "top"
                : signals.category === "bottoms"
                  ? "bottom"
                  : signals.category === "dresses"
                    ? "dress"
                    : signals.category,
            body_shape: normalizeBodyShape(signals.body_shape),
            body_size: normalizeBodySize(signals.body_size),
            height_bucket: signals.height_bucket ?? null,
            brand_fit_tendency:
              signals.brand_fit_tendency === "runs_small" ||
              signals.brand_fit_tendency === "runs_large" ||
              signals.brand_fit_tendency === "true_to_size"
                ? signals.brand_fit_tendency
                : null,
          }
        : null,
  };
}

